'use strict';

/**
 * Player class
 */
class Player {
  /**
   * Constructor
   * @param {Object} options
   * @return {*}
   */
  constructor(options) {
    this.gameId = options.gameId;
    this._options = options;
    this._dbName = `g8plugin-${this.gameId}`;
    this._collection = 'USER_DATA';
    this._db = null;
  }

  databaseReady() {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this._dbName, 1);
      request.onsuccess = (event) => {
        this._db = event.target.result;
        resolve();
      };
      request.onerror = (event) => {
        reject(event.target.error);
      };
      request.onupgradeneeded = (event) => {
        this._db = event.target.result;
        this._db.createObjectStore(this._collection);
      };
    });
  }

  async initPlayer() {
    var playerDb = await this.getDataInIndexedDB(['g8-player']);
    var localPlayer = playerDb['g8-player'];
    var localPlayerToken = localPlayer ? localPlayer.token : null;

    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Game-Key', this.gameId);
    headers.append('Player-Token', localPlayerToken);
    let url = `${this._options.apiDomain}/auth/player/login`;

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
    });

    const result = await response.text();
    let resultJson = JSON.parse(result);
    let token = resultJson.auth?.playerToken;

    if (!resultJson.success || !token) {
      await this.delete();
      throw new Error('Authentication failed');
    }

    let player = resultJson.data;
    player.token = token;
    await this.saveDataToIndexedDB({ 'g8-player': player });
    Object.assign(this, player);
  }

  async delete() {
    indexedDB.deleteDatabase(this._dbName);
  }

  async destroy() {
    this._db && this._db.close();
  }

  async getData(keys) {
    let body = {
      mode: this.mode,
    };
    if (keys && Array.isArray(keys) && keys.length > 0) body.keys = keys;
    return new Promise((resolve, reject) => {
      const headers = new Headers();
      headers.append('Game-Key', this.gameId);
      headers.append('Player-Token', this.token);
      header.append('Content-Type', 'application/json');
      let url = `${this._options.apiDomain}/player/${this.id}/data`;
      fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(body),
      })
        .then((response) => response.text())
        .then((result) => {
          var resultData = JSON.parse(result);
          if (resultData.success) resolve(resultData.data);
          else reject();
        })
        .catch((error) => reject(error));
    });
  }

  async saveData(data) {
    let body = {
      mode: this.mode,
      data: data || {},
    };
    return new Promise((resolve, reject) => {
      const headers = new Headers();
      headers.append('Game-Key', this.gameId);
      headers.append('Player-Token', this.token);
      header.append('Content-Type', 'application/json');
      let url = `${this._options.apiDomain}/player/${this.id}/data`;
      fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
      })
        .then((response) => response.text())
        .then((result) => {
          var resultData = JSON.parse(result);
          if (resultData.success) resolve();
          else reject();
        })
        .catch((error) => reject(error));
    });
  }

  saveDataToIndexedDB(data) {
    return new Promise((resolve, reject) => {
      const transaction = this._db.transaction(this._collection, 'readwrite');
      const objectStore = transaction.objectStore(this._collection);

      const keys = Object.keys(data);
      const promises = keys.map((key) => {
        return new Promise((resolveKey, rejectKey) => {
          const request = objectStore.put(data[key], key);

          request.onsuccess = function (event) {
            resolveKey();
          };

          request.onerror = function (event) {
            rejectKey(event.target.error);
          };
        });
      });

      Promise.all(promises)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getDataInIndexedDB(keys) {
    return new Promise((resolve, reject) => {
      const objectStore = this._db.transaction(this._collection).objectStore(this._collection);

      var result = {};
      if (keys && Array.isArray(keys) && keys.length > 0) {
        const requests = keys.map((key) => {
          return new Promise((resolveKey, rejectKey) => {
            let request = objectStore.get(key);
            request.onsuccess = function (event) {
              result[key] = event.target.result;
              resolveKey();
            };
            request.onerror = function (event) {
              rejectKey(event.target.error);
            };
          });
        });

        Promise.all(requests)
          .then(() => {
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        let request = objectStore.openCursor();
        request.onsuccess = function (event) {
          var cursor = event.target.result;
          if (cursor) {
            result[cursor.key] = cursor.value;
            cursor.continue();
          } else resolve(result);
        };
        request.onerror = function (event) {
          reject(event.target.error);
        };
      }
    });
  }

  getSignature() {
    return this.signature;
  }

  getToken() {
    return this.token;
  }

  getUniqueID() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getPhoto() {
    return this.avatar;
  }

  getGameId() {
    return this.gameId;
  }

  async setName(name) {
    let body = {
      mode: this.mode,
      name: name,
    };
    return new Promise((resolve, reject) => {
      const headers = new Headers();
      headers.append('Game-Key', this.gameId);
      headers.append('Player-Token', this.token);
      header.append('Content-Type', 'application/json');
      let url = `${this._options.apiDomain}/player/${this.id}`;
      fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(body),
      })
        .then((response) => response.text())
        .then((result) => {
          var resultData = JSON.parse(result);
          if (resultData.success) {
            this.name = name;
            resolve();
          } else reject();
        })
        .catch((error) => reject(error));
    });
  }

  setPhoto(avatar) {
    let body = {
      mode: this.mode,
      avatar: avatar,
    };
    return new Promise((resolve, reject) => {
      const headers = new Headers();
      headers.append('Game-Key', this.gameId);
      headers.append('Player-Token', this.token);
      header.append('Content-Type', 'application/json');
      let url = `${this._options.apiDomain}/player/${this.id}`;
      fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(body),
      })
        .then((response) => response.text())
        .then((result) => {
          var resultData = JSON.parse(result);
          if (resultData.success) {
            this.avatar = avatar;
            resolve();
          } else reject();
        })
        .catch((error) => reject(error));
    });
  }

  async sign() {
    return new Promise((resolve, reject) => {
      let url = `${this._options.apiDomain}/player/sign`;
      fetch(url, {
        method: 'POST',
        headers: {
          'Game-Key': this._options.gameId,
          'Player-Token': this.token,
        },
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async logPlayHistory() {
    if (!this.userId) {
      let currentTime = new Date().getTime();
      const recentGameStr = localStorage.getItem('user:recentGames');
      let recentGames = recentGameStr != null ? JSON.parse(recentGameStr) : {};
      recentGames[this._options.gameId] = {
        gameKey: this._options.gameId,
        lastPlayTime: currentTime,
      };
      localStorage.setItem('user:recentGames', JSON.stringify(recentGames));
    }
    const headers = new Headers();
    headers.append('Game-Key', this._options.gameId);
    headers.append('Player-Token', this.token);
    let url = `${this._options.apiDomain}/play-history`;
    fetch(url, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
    }).catch((error) => console.log(error));
  }
}
export default Player;
